"use client";
import { useEffect, useState } from "react";
import { useChatbot } from "../context/ChatbotContext";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import anamvrLogo from "../src/assets/AnamVR-IconNoBg.webp";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  let aboutDropdownTimeout: NodeJS.Timeout;

  const handleAboutMouseEnter = () => {
    clearTimeout(aboutDropdownTimeout);
    setAboutDropdownOpen(true);
  };

  const handleAboutMouseLeave = () => {
    aboutDropdownTimeout = setTimeout(() => {
      setAboutDropdownOpen(false);
    }, 150); // delay close to allow movement
  };

  const {
    openChat,
    closeChat,
    setMobileMenuOpen: setContextMobileMenuOpen,
  } = useChatbot();

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    const newMobileMenuState = !mobileMenuOpen;
    setMobileMenuOpen(newMobileMenuState);
    setContextMobileMenuOpen(newMobileMenuState);

    // Close chatbot when opening mobile menu
    if (newMobileMenuState) {
      closeChat();
    }
  };

  return (
    <>
      <header
        className={`flex items-center min-w-full lg:min-w-5xl mx-auto justify-between py-6 px-6 md:px-14 fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ||
          aboutDropdownOpen ||
          (typeof window !== "undefined" && window.innerWidth < 768)
            ? "bg-[#fafafa]/90 shadow-sm"
            : "bg-transparent"
        }`}
      >
        {/* Left navigation */}
        <nav className="hidden md:flex items-center text-sm lg:text-base font-medium space-x-4 lg:space-x-6">
          <a
            href="/"
            aria-label="Homepage"
            className="text-black hover:text-[#7745b8] transition-colors relative group"
          >
            Home
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#7745b8] transition-all duration-300 group-hover:w-full"></span>
          </a>
          <a
            href="https://shop.anamvr.com"
            aria-label="Shop"
            className="text-black hover:text-[#7745b8] transition-colors relative group"
          >
            Shop
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#7745b8] transition-all duration-300 group-hover:w-full"></span>
          </a>
          <a
            href="blog"
            aria-label="Blog"
            className="text-black hover:text-[#7745b8] transition-colors relative group"
          >
            Blog
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#7745b8] transition-all duration-300 group-hover:w-full"></span>
          </a>
          <a
            href="contact"
            aria-label="Contact"
            className="text-black hover:text-[#7745b8] transition-colors relative group"
          >
            Contact
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#7745b8] transition-all duration-300 group-hover:w-full"></span>
          </a>
          <div
            className="relative"
            onMouseEnter={handleAboutMouseEnter}
            onMouseLeave={handleAboutMouseLeave}
          >
            <a
              className="text-black hover:text-[#7745b8] cursor-pointer transition-colors relative group"
              tabIndex={0}
            >
              About
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#7745b8] transition-all duration-300 group-hover:w-full"></span>
            </a>
          </div>
        </nav>

        {/* Logo (center) */}
        <a
          href="/"
          aria-label="AnamVR Logo"
          className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
        >
          <img
            src={anamvrLogo || "/placeholder.svg"}
            className="w-14 h-auto lg:w-16 transition-transform hover:scale-105 duration-300"
            alt="AnamVR Logo"
          />
        </a>

        {/* Right section buttons */}
        <div className="hidden md:flex items-center text-sm lg:text-base font-medium space-x-4 lg:space-x-6">
          <a
            href="/for-individuals"
            aria-label="Learn more about AnamVR for individuals"
            className="bg-[#7745b8] text-white px-4 py-2 lg:px-5 lg:py-2 rounded-full hover:bg-[#572994] transition-colors shadow-md flex items-center justify-center"
          >
            For Individuals
          </a>
          <a
            href="/for-organisations"
            aria-label="Learn more about AnamVR for organisations"
            className="bg-[#7745b8] text-white px-4 py-2 lg:px-5 lg:py-2 rounded-full hover:bg-[#572994] transition-colors shadow-md flex items-center justify-center"
          >
            For Organisations
          </a>
        </div>

        {/* Mobile view logo and menu */}
        <div className="flex items-center justify-between w-full md:hidden">
          <a href="/" aria-label="Homepage" className="flex items-center">
            <img
              src={anamvrLogo || "/placeholder.svg"}
              className="w-12 h-auto transition-transform hover:scale-105 duration-300"
              alt="AnamVR Logo"
            />
          </a>
          <button
            className="p-2 rounded-full bg-[#fafafa]/80 text-[#7745b8] shadow-sm border border-[#eacfff] hover:bg-[#eacfff] transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Full-width About Dropdown - Apple Style */}
      <div
        className={`fixed left-0 right-0 top-[88px] bg-[#fafafa]/95 backdrop-blur-md border-b border-gray-200 z-40 transition-all duration-300 ease-out delay-150 ${
          aboutDropdownOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
        onMouseEnter={handleAboutMouseEnter}
        onMouseLeave={handleAboutMouseLeave}
      >
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* About AnamVR Section */}
            <div>
              <h3 className="text-lg font-semibold text-black mb-6">
                About AnamVR
              </h3>
              <div className="space-y-4">
                <a
                  href="/about/mission"
                  className="block text-[#2b2b2b] hover:text-[#7745b8] transition-colors py-1"
                >
                  Our Mission
                </a>
                <a
                  href="/about/vision"
                  className="block text-[#2b2b2b] hover:text-[#7745b8] transition-colors py-1"
                >
                  Our Vision & Pillars
                </a>
                <a
                  href="/about/story"
                  className="block text-[#2b2b2b] hover:text-[#7745b8] transition-colors py-1"
                >
                  Our Story & Impact
                </a>
                <a
                  href="/about/research"
                  className="block text-[#2b2b2b] hover:text-[#7745b8] transition-colors py-1"
                >
                  Research & Evidence
                </a>
              </div>
            </div>

            {/* Our People Section */}
            <div>
              <h3 className="text-lg font-semibold text-black mb-6">
                Our People
              </h3>
              <div className="space-y-4">
                <a
                  href="/about/team"
                  className="block text-[#2b2b2b] hover:text-[#7745b8] transition-colors py-1"
                >
                  Meet the Team
                </a>
                <a
                  href="/about/contributors"
                  className="block text-[#2b2b2b] hover:text-[#7745b8] transition-colors py-1"
                >
                  Contributors & Collaborators
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 ${
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleMobileMenu}
      />

      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[85%] max-w-md bg-gradient-to-br from-white to-[#f9f5ff] z-50 shadow-2xl transform transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Menu header with close button properly aligned to right */}
        <div
          className={`flex items-center p-5 border-b border-purple-100 w-full ${isAboutUsOpen ? "justify-between" : "justify-end"}`}
        >
          {isAboutUsOpen && (
            <button
              onClick={() => setIsAboutUsOpen(false)}
              className="p-2 rounded-full bg-gray-200 text-black transition-colors"
              aria-label="Back"
            >
              <ChevronLeft size={28} />
            </button>
          )}
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-full bg-gray-200 text-black transition-colors"
            aria-label="Close menu"
          >
            <X size={28} />
          </button>
        </div>

        {/* Mobile navigation items */}
        <nav className="flex flex-col h-[calc(100%-70px)]">
          <div className="flex-grow p-6 space-y-5 overflow-y-auto">
            {!isAboutUsOpen ? (
              <>
                {[
                  { href: "/", label: "Home" },
                  { href: "/shop", label: "Shop" },
                  { href: "/blog", label: "Blog" },
                  { label: "About", isDropdown: true },
                  { href: "/contact", label: "Contact" },
                ].map((item) => (
                  <a
                    key={item.label}
                    href={item.href || "#"}
                    className="flex flex-row text-xl justify-between w-full items-center font-medium text-[#2b2b2b] hover:text-[#7745b8] py-2 transition-colors duration-200"
                    onClick={(e) => {
                      if (item.isDropdown) {
                        e.preventDefault();
                        setIsAboutUsOpen(true);
                      } else {
                        toggleMobileMenu();
                      }
                    }}
                  >
                    {item.label}
                    {item.isDropdown && (
                      <ChevronRight size={26} className="ml-2" />
                    )}
                  </a>
                ))}
              </>
            ) : (
              <div className="space-y-4">
                {[
                  { href: "/about/mission", label: "Our Mission" },
                  { href: "/about/vision", label: "Our Vision & Pillars" },
                  { href: "/about/story", label: "Our Story & Impact" },
                  { href: "/about/team", label: "Meet the Team" },
                  {
                    href: "/about/contributors",
                    label: "Contributors & Collaborators",
                  },
                  { href: "/about/research", label: "Research & Evidence" },
                ].map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={toggleMobileMenu}
                    className="block text-xl text-[#2b2b2b] hover:text-[#7745b8] visited:text-[#2b2b2b] transition-colors py-2"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          {/* Action buttons - fixed at the bottom */}
          {!isAboutUsOpen && (
            <div className="p-6 mb-10 border-t border-purple-100 space-y-4">
              <a
                href="/for-individuals"
                aria-label="Learn more about AnamVR for individuals"
                className="flex w-full items-center text-lg justify-center bg-[#7745b8] text-white px-6 py-3.5 rounded-2xl hover:bg-opacity-90 transition-all duration-200 shadow-lg hover:shadow-purple-300/50 transform hover:-translate-y-0.5"
                onClick={toggleMobileMenu}
              >
                For Individuals
              </a>
              <a
                href="/for-organisations"
                aria-label="Learn more about AnamVR for organisations"
                className="flex w-full items-center text-lg justify-center bg-[#7745b8] text-white px-6 py-3.5 rounded-2xl hover:bg-opacity-90 transition-all duration-200 shadow-lg hover:shadow-purple-300/50 transform hover:-translate-y-0.5"
                onClick={toggleMobileMenu}
              >
                For Organisations
              </a>
            </div>
          )}
        </nav>
      </div>
    </>
  );
}
